import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData } from "hooks/useApiHooks";
import { Accordion } from "react-bootstrap";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { HR_PERMISSION_PRESETS } from "utils/hrPermissionKeys";

const formatPermissionGroup = (group) => {
  if (!group) return "General";
  const normalized = String(group).replace(/[-_]/g, " ");
  return normalized
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      const upperWords = ["hr", "pos", "api"];
      if (upperWords.includes(word.toLowerCase())) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const getPermissionSummary = (groups) => {
  return groups.reduce(
    (summary, group) => {
      summary.total += group.permissions.length;
      summary.selected += group.permissions.filter((permission) => permission.checked).length;
      return summary;
    },
    { total: 0, selected: 0 }
  );
};

const groupPermissions = (permissions, selected) => {
  return permissions.reduce((acc, permission) => {
    const { group, name, display_name } = permission;
    const isChecked = selected.includes(name);

    const groupName = group || "General";
    const existingGroup = acc.find((item) => item.group === groupName);

    if (existingGroup) {
      existingGroup.permissions.push({ name, display_name, checked: isChecked });
    } else {
      acc.push({
        group: groupName,
        permissions: [{ name, display_name, checked: isChecked }],
        isAllChecked: false,
      });
    }

    return acc;
  }, []).map((group) => {
    group.isAllChecked = group.permissions.every((p) => p.checked);
    return group;
  });
};

const PermissionList = ({ formData, setFormData, initialPermissions }) => {
  const [permissionsOptions, setPermissionsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // 🔹 Ambil daftar permissions dari API
  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    const queryString = buildQueryString({ all: 1 });

    try {
      const response = await getRequest(`/permission${queryString}`);
      if (response?.success) {
        const allPermissions = response.data;
        
        // 🚀 Set permissions dengan auto select jika sudah ada di initialData
        const selectedPermissions = initialPermissions
          ? initialPermissions.map((p) => p.name)
          : [];

        const groupedPermissions = groupPermissions(allPermissions, selectedPermissions);
        setPermissionsOptions(groupedPermissions);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setLoading(false);
    }
  }, [initialPermissions]);

  // 🔹 Ambil daftar permission saat komponen dimuat
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // 🔹 Fungsi update permission saat checkbox diubah
  const handleCheckboxChange = (groupIndex, permissionIndex) => {
    setPermissionsOptions((prev) => {
      const newPermissions = [...prev];
      newPermissions[groupIndex].permissions[permissionIndex].checked =
        !newPermissions[groupIndex].permissions[permissionIndex].checked;

      newPermissions[groupIndex].isAllChecked = newPermissions[groupIndex].permissions.every((p) => p.checked);

      setFormData((prev) => ({
        ...prev,
        permissions: getCheckedPermissions(newPermissions),
      }));

      return newPermissions;
    });
  };

  // 🔹 Fungsi untuk menangani "Select All" dalam satu grup
  const handleCheckAll = (groupIndex) => {
    setPermissionsOptions((prev) => {
      const newPermissions = [...prev];
      const isChecked = !newPermissions[groupIndex].isAllChecked;

      newPermissions[groupIndex].permissions = newPermissions[groupIndex].permissions.map((p) => ({
        ...p,
        checked: isChecked,
      }));

      newPermissions[groupIndex].isAllChecked = isChecked;

      setFormData((prev) => ({
        ...prev,
        permissions: getCheckedPermissions(newPermissions),
      }));

      return newPermissions;
    });
  };

  // 🔹 Ambil daftar permissions yang dicentang
  const getCheckedPermissions = (updatedPermissions) => {
    return updatedPermissions.flatMap((group) =>
      group.permissions.filter((p) => p.checked).map((p) => p.name)
    );
  };

  const filteredPermissionsOptions = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return permissionsOptions;

    return permissionsOptions
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => {
          const haystack = `${permission.display_name || ""} ${permission.name || ""} ${group.group || ""}`.toLowerCase();
          return haystack.includes(keyword);
        }),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [permissionsOptions, search]);

  const summary = useMemo(() => getPermissionSummary(permissionsOptions), [permissionsOptions]);

  const selectedByGroup = (groupItem) => groupItem.permissions.filter((permission) => permission.checked).length;

  return (
    <>
      <div className="card mb-3 border-0 bg-light">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md">
              <label className="form-label mb-1">Cari Permission</label>
              <input
                type="text"
                className="form-control"
                placeholder="Contoh: asset, salary, attendance..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-auto">
              <div className="border rounded bg-white px-3 py-2 text-md-center">
                <div className="text-muted small">Terpilih</div>
                <div className="h3 mb-0">{summary.selected}/{summary.total}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="alert alert-info py-2 small">
        <div className="fw-bold mb-1">Key permission HR yang dipakai kode:</div>
        <div className="d-flex flex-wrap gap-1">
          {HR_PERMISSION_PRESETS.map((permission) => (
            <span key={permission.name} className="badge bg-blue-lt text-blue">
              {permission.name}
            </span>
          ))}
        </div>
      </div>

      <Accordion defaultActiveKey="0">
      {loading ? (
        <p>Loading permissions...</p>
      ) : (
        filteredPermissionsOptions.length > 0 ? (
          filteredPermissionsOptions.map((groupItem, index) => {
            const originalGroupIndex = permissionsOptions.findIndex((group) => group.group === groupItem.group);
            return (
            <Accordion.Item eventKey={index.toString()} key={groupItem.group}>
              <Accordion.Header>
                <div className="d-flex flex-column flex-sm-row justify-content-between w-100 gap-2 pe-3">
                  <span className="d-flex align-items-center flex-wrap gap-1">
                    {formatPermissionGroup(groupItem.group)}
                    <span className="badge bg-secondary-lt text-secondary">
                      {selectedByGroup(groupItem)}/{groupItem.permissions.length}
                    </span>
                  </span>
                  <div className="form-check form-switch d-flex align-items-center ms-0 ms-sm-auto" onClick={(e) => e.stopPropagation()}>
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      id={`group-${groupItem.group}`}
                      checked={groupItem.isAllChecked}
                      onChange={() => handleCheckAll(originalGroupIndex)}
                    />
                    <label className="form-check-label fw-normal me-4" htmlFor={`group-${groupItem.group}`}>
                      Pilih Semua
                    </label>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                {groupItem.permissions.map((permission, permissionIndex) => (
                  <div className="form-check py-2 border-bottom" key={permission.name}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={permission.name}
                      checked={permission.checked}
                      onChange={() => {
                        const originalPermissionIndex = permissionsOptions[originalGroupIndex].permissions.findIndex((item) => item.name === permission.name);
                        handleCheckboxChange(originalGroupIndex, originalPermissionIndex);
                      }}
                    />
                    <label className="form-check-label w-100" htmlFor={permission.name}>
                      <span className="fw-bold d-block">{permission.display_name}</span>
                      <span className="text-muted small">{permission.name}</span>
                    </label>
                  </div>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          );
          })
        ) : (
          <p>No permissions available</p>
        )
      )}
    </Accordion>
    </>
  );
};

const AssignPermission = ({ initialData, onSuccess, onError }) => {
  const API_URL = "/role-permission/sync-permissions";
  const { createData: syncPermissions, loading: isSubmitting } = useCreateData(API_URL, "permissions");

  const [formData, setFormData] = useState({
    role: initialData?.name || "",
    permissions: [],
  });

  useEffect(() => {
    if (initialData?.permissions) {
      setFormData((prev) => ({
        ...prev,
        permissions: initialData.permissions.map((p) => p.name),
      }));
    }
  }, [initialData]);

  const { handleSubmit } = useSubmitHandler(syncPermissions, onSuccess, onError);

  const handleBeforeSubmit = (e) => {
    e.preventDefault();
    handleSubmit(formData, e);
  };

  return (
    <form onSubmit={handleBeforeSubmit} className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      <div className="border rounded bg-light p-3 mb-3">
        <div className="text-muted small">Role yang sedang diatur</div>
        <div className="h3 mb-0">{initialData?.display_name}</div>
        <div className="text-muted small">{initialData?.name}</div>
      </div>

      <div className="action-sheet-content flex-grow-1 overflow-auto pe-1" style={{ minHeight: 0 }}>
        <PermissionList 
          formData={formData} 
          setFormData={setFormData} 
          initialPermissions={initialData?.permissions || []} // ✅ Pastikan ini dikirim
        />
      </div>

      <div className="position-sticky bottom-0 bg-white border-top pt-3 mt-3 pb-2">
        <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
          {isSubmitting ? "Processing..." : "Simpan Permission Role"}
        </button>
      </div>
    </form>
  );
};

export default AssignPermission;
